import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Search, CalendarX } from "lucide-react";
import { fetchAdminExpiredJobs } from "@/lib/api";

const fmtIST = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) : "—");
const inp = "rounded-lg border border-slate-200 px-3 py-2 text-sm min-w-0 focus:outline-none focus:border-emerald-500";

// Read-only: vacancies whose IST last date/time has passed. No edit or delete actions.
export default function AdminExpiredJobs() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState("");
  const [pin, setPin] = useState("");
  const [within, setWithin] = useState("all");
  useEffect(() => { fetchAdminExpiredJobs().then(setD).catch(() => setErr(true)); }, []);
  const items = useMemo(() => {
    if (!d) return [];
    const needle = q.trim().toLowerCase();
    const cutoff = within === "all" ? null : Date.now() - Number(within) * 86400000;
    return d.items.filter((j) => (!needle || `${j.title} ${j.company_name || j.company || ""} ${j.location || ""} ${j.category || ""}`.toLowerCase().includes(needle))
      && (!pin || (j.pincode || "").startsWith(pin)) && (!cutoff || Date.parse(j.deadline_at) >= cutoff));
  }, [d, q, pin, within]);
  if (err) return <div className="text-sm text-rose-700" data-testid="expired-jobs-error">Data unavailable — could not load expired jobs.</div>;
  if (!d) return <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading expired jobs…</div>;
  return (
    <div className="space-y-3" data-testid="admin-expired-jobs">
      <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-xs text-slate-600" data-testid="expired-jobs-note">
        Expired = the job's last date and time (India Standard Time) has passed. View only — nothing can be edited or deleted here. Manually paused/filled or inactive jobs are not listed{d.manually_closed_count ? ` (${d.manually_closed_count} such job${d.manually_closed_count === 1 ? "" : "s"} also past deadline)` : ""}.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_160px] gap-2">
        <label className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, company, location" className={`${inp} w-full pl-9`} data-testid="expired-jobs-search" /></label>
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="PIN code" className={inp} data-testid="expired-jobs-pin" />
        <select value={within} onChange={(e) => setWithin(e.target.value)} className={inp} data-testid="expired-jobs-within">
          <option value="all">Any time</option><option value="7">Expired in last 7 days</option><option value="30">Expired in last 30 days</option>
        </select>
      </div>
      <div className="text-xs text-slate-500" data-testid="expired-jobs-count">{items.length} of {d.items.length} expired job{d.items.length === 1 ? "" : "s"}</div>
      {items.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center" data-testid="expired-jobs-empty">No expired jobs match.</div> : (
        <div className="space-y-2">
          {items.map((j) => (
            <div key={j.id} className="bg-white border border-slate-100 rounded-xl p-3 min-w-0" data-testid={`expired-job-${j.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 break-words">{j.title}</div>
                  <div className="text-[11px] text-slate-500 break-words">{[j.company_name || j.company, j.location, j.pincode && `PIN ${j.pincode}`].filter(Boolean).join(" · ")}</div>
                </div>
                <span className="text-[11px] font-semibold rounded-full bg-rose-50 text-rose-700 px-2 py-0.5 inline-flex items-center gap-1"><CalendarX className="w-3 h-3" /> Expired</span>
              </div>
              <div className="mt-1.5 text-xs text-slate-600" data-testid={`expired-job-deadline-${j.id}`}>Last date: {j.last_date} {j.last_time} IST · ended {fmtIST(j.deadline_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
